"use client";

import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { twMerge } from "tailwind-merge";

import { handleFileUpload } from "@/components/fileCoverInput/inputMethods/fileUpload/handleFileUpload";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { SoftText } from "@/components/text/SoftText";
import { uploadAudioFileTypes } from "@/constants/audio";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { AudioInputState, UserNoDates } from "@/types/types";
import { classNames } from "@/utils/classNames";
import { secondsToPrettyMinutes } from "@/utils/secondsToPrettyMinutes";

export function FileUpload({
  audioContext,
  audioLengthMinLimitMinutes,
  audioLengthMaxLimitMinutes,
  user,
  checkSubscription,
  setInvalidLengthSubscriptionModalOpen,
  setInputFiles,
  currentAudioLengthS,
  multipleFiles,
  maxMultiAudioLengthMinutes,
  className,
}: {
  audioContext: AudioContext | null;
  audioLengthMinLimitMinutes: number | null;
  audioLengthMaxLimitMinutes: number;
  user: UserNoDates | null;
  checkSubscription: boolean;
  setInvalidLengthSubscriptionModalOpen: (value: boolean) => void;
  setInputFiles: AudioInputState["setInputFile"][];
  currentAudioLengthS: number;
  multipleFiles: boolean;
  maxMultiAudioLengthMinutes: number | null;
  className?: string;
}) {
  const [error, setError] = useState<string | null>(null);

  const setAuthModalOpen = useSetAuthModalOpen();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: multipleFiles,
    useFsAccessApi: false,
    onDrop: async (acceptedFiles) => {
      await handleFileUpload({
        files: acceptedFiles,
        user,
        setAuthModalOpen,
        checkSubscription,
        setInvalidLengthSubscriptionModalOpen,
        audioLengthMinLimitMinutes,
        audioLengthMaxLimitMinutes,
        setInputFiles,
        currentAudioLengthS,
        setError,
        maxMultiAudioLengthMinutes,
      });
    },
    accept: uploadAudioFileTypes,
  });

  const maxLimitText = `${secondsToPrettyMinutes(audioLengthMaxLimitMinutes)} max`;

  let audioLimitText: string;
  if (audioLengthMinLimitMinutes === null) {
    audioLimitText = `${maxLimitText}`;
  } else {
    audioLimitText = `${audioLengthMinLimitMinutes} - ${maxLimitText}`;
  }

  return (
    <div
      data-testid="file-upload-dropzone"
      className={twMerge(
        classNames(
          "relative flex cursor-pointer justify-center rounded-3xl border-2 border-dashed border-primary px-4 py-6 transition-colors",
          isDragActive ? "border-primaryLight bg-primaryLight" : "",
          error !== null ? "border-red-500" : "hover:border-primaryLight"
        ),
        className
      )}
      {...getRootProps({
        onClick: async (event) => {
          if (audioContext !== null && audioContext.state === "suspended") {
            await audioContext.resume();
          }
          if (user === null) {
            event.stopPropagation();
            setAuthModalOpen();
          }
        },
      })}
    >
      <div className="text-center">
        <ArrowUpTrayIcon
          className="mx-auto h-12 w-12 text-gray-300"
          aria-hidden="true"
        />
        <div className="mt-4 flex justify-center">
          <input {...getInputProps()} />
          <SmallTitle elementType="p" className="mb-1">
            Add or drop your audio {multipleFiles ? "files" : "file"}
          </SmallTitle>
        </div>
        <SoftText elementType="p">
          WAV, MP3, OGG, M4A, AIFF & FLAC.
          <br />
          {audioLimitText}
        </SoftText>
      </div>

      {error !== null && (
        <NormalText
          elementType="p"
          className="absolute -bottom-4 left-0 h-2 leading-4 text-red-500"
        >
          {error}
        </NormalText>
      )}
    </div>
  );
}
